<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { KendoGrid, KendoGridField } from "@Kendo/Components/KendoGrid";
    import { ellipsis, isNullOrWhitespace, strCompareLowerCase } from "@common/Common";

    @Component({
        props: {
            propOpened: { type: Boolean, required: true, default: false },
            propGrid: { type: Object, required: false, default: null },
        },
        watch: {
            opened: 'onOpenedChanged'
        }
    })
    export default class KendoGridColumnsEditorDialog extends Vue
    {
        //columns - visible columns
        //fields - all fields

        get opened(): boolean { return this.$props.propOpened; }
        get grid(): KendoGrid { return this.$props.propGrid; }

        Fields: FieldVM[] = [];
        Sections: SectionVM[] = [];
        SearchPhrase: string = null;

        get SelectedFields(): FieldVM[] { return this.Fields.filter(f => f.IsSelected); }

        //@Watch('opened')
        onOpenedChanged(val: boolean, oldVal: boolean)
        {
            if (val === true)
            {
                const columns = this.grid.GetOptions().GetColumns()
                    .filter(c => !isNullOrWhitespace(c.title))
                    .filter(c => c.command == null)
                    .filter(c => c.hidden !== true)
                    .map(c => c.field);

                this.Fields = this.grid.GetFields()
                    .filter(f => f.HasColumn)
                    .map(f => new FieldVM(f));

                this.Fields.filter(f => columns.includes(f.Id)).forEach(f => f.IsSelected = true);

                this.Sections = this.Fields
                    .map(f => f.Section)
                    .filter((v, i, sectionsNames) => sectionsNames.indexOf(v) === i)
                    .sort((a, b) => strCompareLowerCase(a, b))
                    .map(sName => new SectionVM(sName, this));
            }
        }

        ToggleColumnCommand(fieldId: string)
        {
            const fieldVM = this.Fields.find(f => f.Id === fieldId);
            fieldVM.IsSelected = !fieldVM.IsSelected;
        }

        AddColumnCommand(fieldId: string)
        {
            this.Fields.find(f => f.Id === fieldId).IsSelected = true;
        }

        RemoveColumnCommand(fieldId: string)
        {
            this.Fields.find(f => f.Id === fieldId).IsSelected = false;
        }

        confirm()
        {
            this.grid.SetColumns(this.SelectedFields.map(f => f.Id));
            this.close();
        } 

        close()
        {
            this.$emit('close-dialog');
        }
    }

    class SectionVM
    {
        Name: string;
        parent: KendoGridColumnsEditorDialog = null;

        get Fields(): FieldVM[] 
        {
            let fields = this.parent.Fields.filter(f => f.Section === this.Name);

            if (!isNullOrWhitespace(this.parent.SearchPhrase))
            {
                const phrase = this.parent.SearchPhrase.toLowerCase();
                fields = fields.filter(f => f.Title.toLowerCase().indexOf(phrase) >= 0);
            }

            return fields.sort((a, b) => strCompareLowerCase(a.Title, b.Title));
        }

        constructor(name: string, parent: KendoGridColumnsEditorDialog)
        {
            this.parent = parent;
            this.Name = name;
        }
    }

    class FieldVM
    {
        Id: string;
        Title: string;
        Section: string;
        IsSelected: boolean = false;
        TitleEllipsis: string;

        constructor(field: KendoGridField)
        {
            this.Id = field.FieldId;
            this.Title = field.ColumnTitle;
            this.Section = field.Section;
            this.TitleEllipsis = ellipsis(this.Title, 52);
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Choose which columns you see</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="row">

                            <div class="col-md-6">
                                <div class="input-group">
                                    <input type="search" placeholder="Search..." class="form-control" v-model="SearchPhrase">
                                    <span class="input-group-append">
                                        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                    </span>
                                </div>
                                <div style="max-height: 500px; height: 500px; overflow-y: auto;" class="mt-3">
                                    <div v-for="s in Sections" :key="s.Name">
                                        <h6 class="mt-3 mb-1">{{s.Name}}</h6>
                                        <div v-for="f in s.Fields" :key="f.Id">
                                            <a class="dropdown-item" @click="ToggleColumnCommand(f.Id)">
                                                <i class="fa-regular fa-lg" :class="f.IsSelected ? 'fa-square-check' : 'fa-square'"></i>
                                                <span :title="f.Title">{{f.TitleEllipsis}}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div class="col-md-6">
                                <h5>Selected columns ({{SelectedFields.length}}):</h5>
                                <div style="max-height: 500px; height: 500px; overflow-y: auto;">
                                    <div v-for="f in SelectedFields" :key="f.Id" class="p-2 bg-secondary m-2 text-white">
                                        <span :title="f.Title">{{f.TitleEllipsis}}</span>
                                        <button type="button" class="p-0 border-0 text-white" style="background-color: transparent; font-size: 1.3rem; float: right;"
                                                @click="RemoveColumnCommand(f.Id)">
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                        <button type="button" @click="confirm" class="btn btn-primary">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>